import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Users, Trash2, Loader2, MailCheck, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  awaiting_confirmation: { label: 'Check your email to confirm', className: 'bg-amber-100 text-amber-700 border-amber-300', Icon: Clock },
  invited: { label: 'Invite sent — awaiting password setup', className: 'bg-blue-100 text-blue-700 border-blue-300', Icon: MailCheck },
};

export const FamilyInvites = () => {
  const [data, setData] = useState({ can_invite: true, invites: [] });
  const [form, setForm] = useState({ name: '', email: '' });
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchInvites = async () => {
    try {
      const r = await axios.get(`${API}/family/invites`);
      setData(r.data);
    } catch (e) {
      // silently hide for non-clients
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSending(true);
    setFormError('');
    try {
      await axios.post(`${API}/family/invite`, { name: form.name.trim(), email: form.email.trim() });
      toast.success('Check your email to confirm this invite!');
      setForm({ name: '', email: '' });
      fetchInvites();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to send invite';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (inviteId) => {
    try {
      await axios.delete(`${API}/family/invites/${inviteId}`);
      toast.success('Member removed');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (!loaded) return null;

  return (
    <div className="glass-card p-4 sm:p-6 mb-4" data-testid="family-invites-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-500/20 rounded-lg">
          <Users className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <p className="font-semibold text-white">My Family & Friends</p>
          <p className="text-xs text-slate-400">Invite someone to view your projects</p>
        </div>
      </div>

      {data.can_invite && (
        <form onSubmit={handleInvite} className="space-y-3 mb-4">
          <div>
            <Label htmlFor="fam-name" className="text-sm">Their Name</Label>
            <Input
              id="fam-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. John Smith"
              className="mt-1"
              data-testid="family-invite-name-input"
            />
          </div>
          <div>
            <Label htmlFor="fam-email" className="text-sm">Their Email</Label>
            <Input
              id="fam-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="e.g. john@email.com"
              className="mt-1"
              data-testid="family-invite-email-input"
            />
          </div>
          {formError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/40" data-testid="family-invite-error">
              <p className="text-sm text-red-400 font-medium">{formError}</p>
            </div>
          )}
          <Button
            type="submit"
            disabled={sending || !form.name.trim() || !form.email.trim()}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 min-h-[44px]"
            data-testid="family-invite-submit-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Send Invite
          </Button>
          <p className="text-xs text-slate-400">
            You'll get a confirmation email first — the invite is only sent after you confirm.
          </p>
        </form>
      )}

      {data.invites.length > 0 && (
        <div className="space-y-2" data-testid="family-invites-list">
          {data.invites.map((invite) => {
            const cfg = STATUS_CONFIG[invite.status] || { label: 'Active', className: 'bg-green-100 text-green-700 border-green-300', Icon: Users };
            const StatusIcon = cfg.Icon;
            return (
              <div
                key={invite.id}
                className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50"
                data-testid={`family-invite-item-${invite.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-gray-900 text-sm font-medium truncate">{invite.invitee_name}</p>
                  <p className="text-xs text-gray-500 truncate">{invite.invitee_email}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 mt-1 rounded-full border ${cfg.className}`}>
                    <StatusIcon className="w-3 h-3" />
                    {cfg.label}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemove(invite.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0 flex-shrink-0"
                  data-testid={`remove-family-invite-${invite.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FamilyInvites;
